<template>
  <div class="threeDrawing-wrapper">
    <div
      ref="threeDrawing"
      class="threeDrawing"
      @pointerdown="pointerDown"
      @click="clickEvent"
    />
    <!-- Legenda fixa: orientação dos eixos e escala da grade -->
    <div class="threeDrawing-legend">
      <span style="color: #e53935">x</span> ·
      <span style="color: #43a047">y</span> ·
      <span style="color: #1e88e5">z</span> axes (1 µm) · grid 1 µm ·
      <span style="color: #f9a825">◆ source</span> (arrow = polarization x)
    </div>
  </div>
</template>

<script>

import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { mapActions, mapGetters } from 'vuex';
import geometries from './geometries';
import md5 from 'js-md5';
import formatColor from '@/common/formatColor';

const { SphereGeometry } = geometries;

// Formas definidas pela caixa largura x altura x profundidade (mesmos nomes aceitos pelo Gmsh)
const SOLID_GEOMETRIES = {
  block: geometries.BoxGeometry,
  triangle: geometries.TriangleGeometry,
  lozangle: geometries.LozangleGeometry,
  trapeze: geometries.TrapezeGeometry,
  cylinder: geometries.CylinderGeometry,
  cone: geometries.ConeGeometry,
  pyramid: geometries.PyramidGeometry,
  ring: geometries.RingGeometry
};

export default {
  name: 'ThreeDrawing',
  mounted() {
    this.init();
    this.renderScene();
    // O canvas ocupa a área central inteira: acompanha o tamanho dela (janela, painéis, rotação)
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.$refs.threeDrawing);
  },
  beforeDestroy() {
    // Sem isso o laço de animação e o WebGL continuavam rodando depois de sair do simulador
    this.stopped = true;
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.renderer) this.renderer.dispose();
  },
  watch: {
    // selectedToken: function() {
    //   if (this.mode == "transform") {
    //     this.updateTransformer();
    //   }
    // },
    // viewMode: function() {
    //   this.updateTransformer();
    // },
    // mode: function() {
    //   this.updateTransformer();
    // },
    GeometryList: {
      handler() {
        this.renderScene();
      },
      deep: true
    },
    SourcesList: {
      handler() {
        this.renderScene();
      },
      deep: true
    },
  },
  methods: {
    init() {   
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xededed)
      this.camera = new THREE.PerspectiveCamera(75, 
        this.$refs.threeDrawing.offsetWidth /
        this.$refs.threeDrawing.offsetHeight);
      this.renderer = new THREE.WebGLRenderer();
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.renderer.setSize(
        this.$refs.threeDrawing.offsetWidth, 
        this.$refs.threeDrawing.offsetHeight
      );
      this.$refs.threeDrawing.appendChild(this.renderer.domElement);    
      // Vista em ângulo: a grade do chão (plano y = 0) e os três eixos ficam visíveis
      this.camera.position.set(4, 3, 6);
      this.camera.lookAt(0, 0, 0); 
      this.directionalLight = new THREE.DirectionalLight(0xffffff, 5);
      this.directionalLight2 = new THREE.DirectionalLight(0xffffff, 5);
      this.directionalLight.position.set(9.9, 10, 10.1);
      this.directionalLight2.position.set(-9.9, -10, -10.1);
      //this.camera.add(this.directionalLight);
      this.scene.add(this.directionalLight,this.directionalLight2); 
      // Referências: eixos de 1 µm (x vermelho, y verde, z azul) e grade de 1 µm no plano y = 0
      const grid = new THREE.GridHelper(10, 10, 0x9e9e9e, 0xcfcfcf);
      const axes = new THREE.AxesHelper(1);
      axes.material.depthTest = false;
      axes.renderOrder = 1;
      this.scene.add(grid, axes);
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.transformControls = new TransformControls(this.camera, this.renderer.domElement);
      this.transformControls.setMode("scale");
      this.scene.add(this.transformControls);

      this.animate(); 
    },
    clearScene() {
      // Remove geometrias e fontes (e as setas de polarização), mantendo luzes, eixos e grade
      const filteredSceneChildren = this.scene.children.filter(child => {
        return !(child instanceof THREE.Mesh) && !child.userData.dynamic;
      });
      this.scene.children = filteredSceneChildren;
    },
    createTransformControls(){
      const geometries = this.scene.children.filter(child => {
        return (child instanceof THREE.Mesh);
      });
      geometries.map(geometry => {
        this.transformControls.attach(geometry.mesh);
      })
    },
    renderScene() {
      this.clearScene();
      this.loadGeometries();
      this.loadSources();
      this.updateState();
    },
    animate() {
      if (this.stopped) return;
      this.frame = requestAnimationFrame(this.animate);
      this.renderer.render(this.scene, this.camera);
    },
    resize() {
      const element = this.$refs.threeDrawing;
      if (!element || !this.renderer) return;
      const width = element.clientWidth, height = element.clientHeight;
      if (!width || !height) return;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    },
    onWindowResize() {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    },
    pointerDown(event) {
      this.pointerStart = { x: event.clientX, y: event.clientY };
    },
    clickEvent(event) {
      // Girar ou mover a cena (OrbitControls) também termina num "click": só conta como clique se o
      // ponteiro quase não se moveu. Antes, girar a cena com a ferramenta de criar ativa inseria um cubo.
      const start = this.pointerStart;
      this.pointerStart = null;
      if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5) return;
      if (this.mode == "create") {
        this.addGeometry();
      } else if (this.mode == "sources") {
        this.addSource();
      }
    },
    loadGeometries() {
      this.GeometryList.map(geometry => {
        const color = formatColor(geometry.fill);
        const positions = { x: geometry.x, y: geometry.y, z: geometry.z };
        let geo;

        if (geometry.shape == "sphere") {
          geo = new SphereGeometry(color, Number(geometry.radius), positions);
        } else {
          const GeometryClass = SOLID_GEOMETRIES[geometry.shape];
          if (!GeometryClass) {
            console.warn("Forma 3D desconhecida:", geometry.shape);
            return;
          }
          geo = new GeometryClass(
            color,
            {
              width: Math.abs(Number(geometry.width)),
              height: Math.abs(Number(geometry.height)),
              depth: Math.abs(Number(geometry.depth))
            },
            positions
          );
        }

        this.scene.add(geo.mesh);
      });
    },

    loadSources() {
      // Fontes sempre em amarelo (as geometrias usam a cor do material) e com uma seta na direção x,
      // a polarização da corrente gaussiana do NGSolve
      const color = formatColor("#F9A825FF");
      this.SourcesList.map(source => {
        const position = { x: Number(source.x) || 0, y: Number(source.y) || 0, z: Number(source.z) || 0 };
        const sourceGeometry = new SphereGeometry(color, 0.12, position);
        this.scene.add(sourceGeometry.mesh);
        const arrow = new THREE.ArrowHelper(
          new THREE.Vector3(1, 0, 0),
          new THREE.Vector3(position.x - 0.35, position.y, position.z),
          0.7, 0xf9a825, 0.2, 0.12
        );
        arrow.userData.dynamic = true;
        this.scene.add(arrow);
      });
    },
    addGeometry(/*event*/) {
        let token = md5.hex(Date.now().toString());

        this.setCurrentGeometryAction({
            name: this.shape + " " + (this.GeometryList.length + 1),
            token: token,
            x: 0, 
            y: 0,
            z: 0, 
            fill: this.color,
            shape: this.shape,
            epsilon: 12,
            class: "geometry",
            width: 1,
            height: 1,
            depth: 1,
            materialId: 0,
            radius: 1,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
            scaleZ: 1,
            geometricFill: {
                properties: {
                    name: `Default ${this.shape + " " + (this.GeometryList.length + 1)}`,
                    width: 1,
                    height: 1,
                    depth: 1,
                    image: ''
                },
                elements: []
            },
            geometricFillPattern: []
        });

        // this.setDrawingStatusAction(true);
        this.GeometryListAppendAction(this.currentGeometry);
        // Um objeto por clique na ferramenta: volta para a seleção e abre as propriedades do novo
        this.setCurrentMode("transform");
        this.setSelectedTokenAction(token);
        
    },
    addSource() {
      const token = md5.hex(Date.now().toString());

      const name = "Source " + (this.SourcesList.length + 1);
      const fill = "#FF0000FF";

      const newSource = {
        name,
        fill,
        token,
	class: "source",
        // Mesmos padrões usados pelo NGSolve (µm)
        waveLength: 1.55,
        waveWidth: 0.4,
        x: 0,
        y: 0,
        z: 0
      }

      this.SourcesListAppend(newSource);
      this.setCurrentMode("transform");
      this.setSelectedTokenAction(token);
      // console.log("aa",this.SourcesList);
    },
    ...mapActions("simulator", [
      "GeometryListAppendAction",
      "setGeometryList",
      "updateState",
      "setDrawingStatusAction",
      "setState",
      "setSelectedTokenAction",
      "setCurrentMode",
      "setCurrentGeometryAction",
      "ZOOM",
      "keyPressAction",
      "keySolveAction",
      "GeometryListRemove",
      "setCurrentGeometryData",
      "setCurrentGeometrySizeWidth",
      "setCurrentGeometrySizeHeight",
      "setCurrentGeometrySizeDepth",
      "setCurrentGeometryRadius",
      "SourcesListAppend",
      "setCurrentGeometryPosX",
      "setCurrentGeometryPosY",
      // "FluxListAppend",
      "setCurrentGeometryRotation"
    ]),
  },
  computed: {
    ...mapGetters("simulator", [
      "id",
      "getCurrentScale",
      "GeometryList",
      "drawing",
      "currentGeometry",
      "mode",
      "zoomAction",
      "currentScale",
      "shape",
      "color",
      "gridVisible",
      "geometryData",
      "selectedToken",
      "SourcesList",
      "viewMode",
      "cell",
      "dimensions",
      "scene_simulation",
      "view_simulation",
      // "FluxList",
      "showMiniCanvas"
    ]),
  }
}


</script>

<style lang="scss">

.threeDrawing-wrapper {
  position: relative;
  display: inline-block;
}
.threeDrawing-legend {
  position: absolute;
  left: 8px;
  bottom: 6px;
  padding: 2px 6px;
  font-size: 11px;
  color: #333;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 3px;
  pointer-events: none;
}
.threeDrawing canvas {
  display: block;
}
.threeDrawing {
  width: 600px;
  height: 600px;
  // Em telas menores o canvas encolhe mantendo o formato quadrado
  max-width: calc(100vw - 16px);
  max-height: calc(100vw - 16px);
}

</style>
